import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';

const LazyImage = ({ 
  src, 
  alt, 
  width, 
  height, 
  sx = {}, 
  skeletonHeight = 200,
  objectFit = 'contain',
  ...props 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const imgRef = useRef();

  // Konvertera bildkälla till WebP om möjligt
  const getOptimizedImageUrl = useCallback((url) => {
    // Om det är en extern URL (t.ex. Amazon) eller en placeholder, använd den som den är
    if (!url || url.includes('placeholder') || url.startsWith('data:')) {
      return url;
    }

    // Om det redan är en WebP-bild, använd den som den är
    if (url.toLowerCase().endsWith('.webp')) {
      return url;
    }

    // För lokala bilder, försök använda WebP-versionen om den finns
    try {
      // Enkel kontroll för att se om det är en lokal bild
      if (url.startsWith('/')) {
        // Försök hitta WebP-versionen (detta är en förenklad implementation)
        const basePath = url.substring(0, url.lastIndexOf('.')) || url;
        return `${basePath}.webp`;
      }
      return url;
    } catch (error) {
      console.error('Error optimizing image URL:', error);
      return url;
    }
  }, []);

  useEffect(() => {
    if (src) {
      setImageSrc(getOptimizedImageUrl(src));
    }
  }, [src, getOptimizedImageUrl]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '100px' // Öka rootMargin för att ladda bilder tidigare
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
  }, []);

  const handleError = useCallback(() => {
    console.warn(`Failed to load image: ${src}`);
    setHasError(true);
    setIsLoaded(true);
  }, [src]);

  return (
    <Box
      ref={imgRef}
      sx={{
        position: 'relative',
        width: width || '100%',
        height: height || skeletonHeight,
        overflow: 'hidden',
        ...sx
      }}
      {...props}
    >
      {!isLoaded && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height={skeletonHeight}
          animation="wave"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            borderRadius: '4px'
          }}
        />
      )}
      
      {isInView && (
        <picture>
          {/* Försök använda WebP om det stöds */}
          <source srcSet={imageSrc} type="image/webp" />
          {/* Fallback till originalbilden */}
          <img
            src={hasError ? '/images/placeholder-image.svg' : src}
            alt={alt || 'Produktbild'}
            onLoad={handleLoad}
            onError={handleError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: objectFit,
              opacity: isLoaded ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              position: 'absolute',
              top: 0,
              left: 0,
              zIndex: 2
            }}
            loading="lazy"
            decoding="async" // Lägg till async decoding för bättre prestanda
          />
        </picture>
      )}
      
      {hasError && isLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 3,
            textAlign: 'center',
            color: 'text.secondary',
            padding: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '4px'
          }}
        >
          <Typography variant="caption" component="p">
            Bild kunde inte laddas
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LazyImage;